import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Image, Input, message, Modal, Select, Space, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import { getBenefitsListApi, sortBenefitApi } from '@/api/member/member';
import { BENEFITS_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';

const { RangePicker } = DatePicker;

const benefitTypeOptions = [
  { label: '基础权益', value: 'BASIC' },
  { label: '其他权益', value: 'OTHER' },
  { label: '店铺权益', value: 'SHOP' },
];

const allOpenLevelOptions = [
  { label: '一级会员', value: '1' },
  { label: '二级会员', value: '2' },
  { label: '三级会员', value: '3' },
  { label: '四级会员', value: '4' },
  { label: '五级会员', value: '5' },
];

const showStatusOptions = [
  { label: '展示', value: '0' },
  { label: '隐藏', value: '1' },
];

const parseIcon = (json?: string) => {
  if (!json) return '';
  try {
    const o = JSON.parse(json);
    return o.tmUrl || o.jdUrl || '';
  } catch (err) {
    return '';
  }
};

const typeLabel = (t?: string) => {
  if (t === 'BASIC') return '基础权益';
  if (t === 'OTHER') return '其他权益';
  if (t === 'SHOP') return '店铺权益';
  return '—';
};

const levelLabel = (lv?: string) => {
  if (!lv) return '—';
  const map: any = { '1': '一级会员', '2': '二级会员', '3': '三级会员', '4': '四级会员', '5': '五级会员' };
  return map[lv] || lv;
};

const MembershipLevelBenefits: React.FC = () => {
  const { currentShopId } = useSnapshot(state);
  const openLevelOptions = useMemo(() => allOpenLevelOptions, []);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const canSortUp = useMemo(() => checkPermissions(BENEFITS_KEY.SORT_UP), []);
  const canSortDown = useMemo(() => checkPermissions(BENEFITS_KEY.SORT_DOWN), []);

  const renderValidity = (record: any) => {
    if (record?.validityType === 0) return '长期';
    const start = record?.validityStart ? dayjs(record.validityStart).format('YYYY-MM-DD HH:mm:ss') : '';
    const end = record?.validityEnd ? dayjs(record.validityEnd).format('YYYY-MM-DD HH:mm:ss') : '';
    if (start && end) return `${start} ~ ${end}`;
    if (start || end) return start || end;
    return '—';
  };

  const columns = useMemo(
    () => [
      { title: '权益名称', dataIndex: 'benefitsName' },
      {
        title: '缩略图',
        dataIndex: 'iconUrlJson',
        render: (val: string) => {
          const url = parseIcon(val);
          return url ? (
            <Image
              src={url}
              width={40}
              height={40}
              preview={{ mask: null }}
            />
          ) : (
            <span>—</span>
          );
        },
      },
      { title: '权益类型', dataIndex: 'benefitsType', render: (t: string) => <Tag>{typeLabel(t)}</Tag> },
      {
        title: '有效期',
        dataIndex: 'validityType',
        render: (_: number, record: any) => renderValidity(record),
      },
      { title: '解锁等级', dataIndex: 'openLevel', render: (v: string) => <Tag>{levelLabel(v)}</Tag> },
      {
        title: '是否C端展示',
        dataIndex: 'showStatus',
        render: (v: number) => (v === 0 ? <Tag color="green">展示</Tag> : <Tag>隐藏</Tag>),
      },
      {
        title: '最近启用时间',
        dataIndex: 'recentOpenTime',
        render: (v: string) => v || '—',
      },
      {
        title: '操作',
        render: (_: any, record: any, index: number) => (
          <Space size={12}>
            {canSortUp && (
              <Button
                type="link"
                onClick={() => handleSort(record, 'UP')}
                disabled={index === 0}
                style={{ padding: 0 }}>
                上移
              </Button>
            )}
            {canSortDown && (
              <Button
                type="link"
                onClick={() => handleSort(record, 'DOWN')}
                disabled={index === dataSource.length - 1}
                style={{ padding: 0 }}>
                下移
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [dataSource.length, canSortUp, canSortDown],
  );

  const fetchList = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const params: any = {
      benefitsType: values.benefitsType || 'ALL',
      benefitsName: values.benefitsName || '',
      openLevel: values.openLevel || '',
      showStatus: values.showStatus || '',
      recentOpenStartTime:
        values.openTime && values.openTime.length ? dayjs(values.openTime[0]).format('YYYY-MM-DD HH:mm:ss') : '',
      recentOpenEndTime:
        values.openTime && values.openTime.length ? dayjs(values.openTime[1]).format('YYYY-MM-DD HH:mm:ss') : '',
    };
    setLoading(true);
    try {
      const res = await getBenefitsListApi(currentShopId, params);
      if (res?.code === 200) {
        setDataSource(res.data || []);
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSort = async (record: any, sortType: 'UP' | 'DOWN') => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      const res = await sortBenefitApi(currentShopId, { sortType, benefitsId: record.id || record.benefitsId });
      if (res?.code === 200) {
        message.success('操作成功');
        fetchList();
      } else {
        message.error(res?.msg || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  const handleReset = () => {
    form.resetFields();
    fetchList();
  };

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12, columnGap: 12 }}>
        <Form.Item
          label="权益名称"
          name="benefitsName">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 200 }}
          />
        </Form.Item>
        <Form.Item
          label="权益类型"
          name="benefitsType">
          <Select
            options={benefitTypeOptions}
            style={{ width: 160 }}
            placeholder="全部"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="解锁等级"
          name="openLevel">
          <Select
            options={openLevelOptions}
            style={{ width: 140 }}
            placeholder="全部"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="是否C端展示"
          name="showStatus">
          <Select
            options={showStatusOptions}
            style={{ width: 140 }}
            placeholder="全部"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="启用时间"
          name="openTime">
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
          />
        </Form.Item>
        <Form.Item>
          <Space size={12}>
            <Button
              type="primary"
              onClick={fetchList}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey={record => record.id || record.benefitsName}
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
      />
    </div>
  );
};

export default MembershipLevelBenefits;
